import React, { useState, useEffect, useRef } from 'react';

// Router
import { NavLink, useLocation } from 'react-router-dom';

// Mui Components
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';

// Icons
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import ImportExportOutlinedIcon from '@mui/icons-material/ImportExportOutlined';
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import MenuOpenOutlinedIcon from '@mui/icons-material/MenuOpenOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import WebhookOutlinedIcon from '@mui/icons-material/WebhookOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';

// Styles
import './styles.scss';

const SideBarNew = ({ currentMode, isImpersonatedMode }) => {
  // states
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const sidebarRef = useRef(null);
  const location = useLocation();

  // Function to dispatch sidebar state change event
  const dispatchSidebarStateChange = (collapsed, expanded) => {
    const event = new CustomEvent('sidebarStateChange', {
      detail: { collapsed, expanded },
    });
    window.dispatchEvent(event);
  };

  const menuSections = [
    {
      title: 'MY ACCOUNT',
      items: [
        {
          label: 'Profile Settings',
          icon: <AccountCircleOutlinedIcon />,
          key: 'Profile Settings',
          link: 'account/profile',
        },
      ],
    },
    {
      title: 'COMPANY',
      items: [
        {
          label: 'My Company',
          icon: <BusinessOutlinedIcon />,
          key: 'My Company',
          link: 'my-company',
        },
        // {
        //   label: 'Sub-Companies',
        //   icon: <SubCompanyCIcon className='multiple-users-icon'/>,
        //   key: 'Sub-Companies',
        //   link: '',
        // },
        // {
        //   label: 'General Settings',
        //   icon: <GeneralSettingsCIcon className="general-settings-icon" />,
        //   key: 'General Settings',
        //   link: 'settings/general',
        // },
        {
          label: 'Manage Users',
          icon: <GroupOutlinedIcon />,
          key: 'Manage Users',
          link: 'account/users',
        },
        {
          label: 'Custom Fields',
          icon: <DataObjectOutlinedIcon />,
          key: 'Custom Fields',
          link: 'settings/custom-fields',
        },
        {
          label: 'Tags',
          icon: <SellOutlinedIcon />,
          key: 'Tags',
          link: 'settings/tags',
        },
        {
          label: 'Imports/Exports',
          icon: <ImportExportOutlinedIcon />,
          key: 'Imports/Exports',
          link: 'account/imports-exports',
        },
        // {
        //   label: 'Tracking Phone Numbers',
        //   icon: <CallCIcon className="user-circle-icon" />,
        //   key: 'Tracking Phone Numbers',
        //   link: '/phone-tracking',
        // },
        {
          label: 'API Keys',
          icon: <KeyOutlinedIcon />,
          key: 'API Keys',
          link: 'settings/api-keys',
        },
        {
          label: 'Webhooks',
          icon: <WebhookOutlinedIcon />,
          key: 'Webhooks',
          link: 'settings/webhooks',
        },
      ],
    },
    {
      title: 'ENGAGEMENT',
      items: [
        {
          label: 'Tracking Phone Numbers',
          icon: <PhoneOutlinedIcon />,
          key: 'Tracking Phone Numbers',
          link: '/phone-tracking',
        },
        {
          label: 'QR Codes',
          icon: <QrCode2OutlinedIcon />,
          key: 'QR Codes',
          link: '/engagement/qr-codes',
        },
        {
          label: 'PURLs',
          icon: <InsertLinkOutlinedIcon />,
          key: 'PURLs',
          link: '/engagement/purls',
        },
        // {
        //   label: 'Web to Mail Connect',
        //   icon: <MailboxCIcon className='user-circle-icon'/>,
        //   key: 'Web to Mail Connect',
        //   link: '',
        // },
      ],
    },
    {
      title: 'BILLING & PLAN',
      items: [
        {
          label: 'Manage Wallet',
          icon: <AccountBalanceWalletOutlinedIcon />,
          key: 'Manage Wallet',
          link: 'account/wallet',
        },
        {
          label: 'Subscription Plan',
          icon: <WorkspacePremiumOutlinedIcon />,
          key: 'Subscription Plan',
          link: 'account/plan',
        },
      ],
    },
  ];

  // functions
  const handleItemClick = () => {
    // Close sidebar on mobile when item is clicked
    if (isCollapsed && isExpanded) {
      setIsExpanded(false);
      dispatchSidebarStateChange(isCollapsed, false);
    }
  };

  const handleToggleSidebar = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    dispatchSidebarStateChange(isCollapsed, newExpandedState);
  };

  const shouldShowContent = !isCollapsed || isExpanded;

  const getItemFullPath = (item) => {
    if (!item?.link) return '';
    return item.link.startsWith('/') ? item.link : `/${item.link}`;
  };

  const isItemActive = (item) => {
    const currentPath = location.pathname;
    const itemPath = getItemFullPath(item);

    // Special case: Imports/Exports item should also be active on contacts import
    if (item.key === 'Imports/Exports') {
      return currentPath === '/account/imports-exports' || currentPath === '/contacts/import';
    }

    if (item.key === 'QR Codes') {
      return (
        currentPath === '/engagement/qr-codes' ||
        currentPath.startsWith('/engagement/qr-code-history')
      );
    }

    if (item.key === 'PURLs') {
      return (
        currentPath === '/engagement/purls' ||
        currentPath.startsWith('/engagement/purl-history')
      );
    }

    return currentPath === itemPath;
  };

  const getSidebarClass = () => {
    if (isImpersonatedMode) {
      return 'impersonatedMode';
    } else {
      return '';
    }
  };

  // Check screen size on mount and resize
  useEffect(() => {
    const handleResize = () => {
      const shouldCollapse = window.innerWidth <= 900;
      setIsCollapsed(shouldCollapse);
      if (!shouldCollapse) {
        setIsExpanded(false);
      }
      dispatchSidebarStateChange(shouldCollapse, isExpanded);
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [isExpanded]);

  // Close sidebar when clicking outside (mobile only)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isCollapsed &&
        isExpanded &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)
      ) {
        setIsExpanded(false);
        dispatchSidebarStateChange(isCollapsed, false);
      }
    };

    if (isCollapsed && isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isCollapsed, isExpanded]);

  return (
    <>
      <Paper
        ref={sidebarRef}
        elevation={0}
        className={`sidebarWrapper ${getSidebarClass()} ${isCollapsed ? 'collapsed' : ''} ${isExpanded ? 'expanded' : ''}`}
      >
        {/* Toggle Button - Only show below 900px */}
        {isCollapsed && (
          <Box className="toggleButtonContainer">
            <Tooltip title={isExpanded ? 'Collapse Sidebar' : 'Expand Sidebar'} placement="right">
              <IconButton
                onClick={handleToggleSidebar}
                className="toggleButton"
                size="small"
                sx={{
                  transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
                }}
              >
                <MenuOpenOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <Box className="sidebarContent" sx={{ padding: '32px 16px 16px' }}>
          {menuSections.map((section, sectionIndex) => (
            <Box key={section.title} sx={{ mb: 2 }}>
              {/* Section Header */}
              {shouldShowContent && (
                <Typography variant="caption" className="sectionHeader">
                  {section.title}
                </Typography>
              )}

              {/* Section Items */}
              <List sx={{ p: shouldShowContent ? 0 : '16px 0 0' }}>
                {section.items.map((item) => (
                  <ListItem key={item.key} sx={{ p: 0, mb: 0.5 }}>
                    <Tooltip title={isCollapsed && !isExpanded ? item.label : ''} placement="right">
                      <ListItemButton
                        onClick={handleItemClick}
                        className={`sectionItem ${isItemActive(item) ? 'active' : ''}`}
                        disableRipple
                        selected={isItemActive(item)}
                        component={NavLink}
                        to={item.link}
                      >
                        <ListItemIcon
                          className="sectionItemIcon"
                          sx={{
                            minWidth: shouldShowContent ? 26 : 24,
                            color: 'inherit',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        {shouldShowContent && (
                          <ListItemText
                            primary={item.label}
                            className={`sectionItemText ${isItemActive(item) ? 'active' : ''}`}
                          />
                        )}
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </Box>
      </Paper>
    </>
  );
};

export default SideBarNew;
